/**
 * MonitorDashboardPage — `/monitor/dashboard`. Public, no auth, no shell.
 *
 * The client's own dashboard. On load it fetches the client's signed insights
 * with the saved token, renders the Critical / Worth-a-look / OK summary as big
 * cards, then the insight list (plain-language message, time, and a per-row
 * "Verify" button that re-checks the public signature and shows ✓ Verified /
 * ✗ Tampered). Empty state invites connecting a source.
 *
 * Redirects to `/monitor/start` when no token is saved.
 */

import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import {
  getMonitorToken,
  getWorkspaceName,
  fetchInsights,
  verifyResult,
  getAlertChannels,
  setAlertChannel,
  testAlert,
  type MonitorInsight,
  type AlertChannels,
} from './monitorClient';

const ink = '#111827';
const inkMuted = '#4b5563';
const inkSubtle = '#6b7280';
const rim = '#e5e7eb';
const accent = '#1d4ed8';

const SEVERITY_STYLE: Record<string, { fg: string; bg: string; border: string; label: string }> = {
  ok: { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Looking good' },
  warning: { fg: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Worth a look' },
  critical: { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'Needs attention' },
};

function severityStyle(s: string) {
  return SEVERITY_STYLE[s] ?? { fg: '#374151', bg: '#f9fafb', border: '#e5e7eb', label: s };
}

function fmtTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Verify affordance ───────────────────────────────────────────────────────

function VerifyButton({ verifyUrl }: { verifyUrl: string }) {
  const [state, setState] = useState<'idle' | 'checking' | 'verified' | 'tampered' | 'error'>(
    'idle',
  );

  async function run() {
    setState('checking');
    try {
      const ok = await verifyResult(verifyUrl);
      setState(ok ? 'verified' : 'tampered');
    } catch {
      setState('error');
    }
  }

  if (state === 'verified') {
    return <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>✓ Verified</span>;
  }
  if (state === 'tampered') {
    return <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>✗ Tampered</span>;
  }
  return (
    <button
      type="button"
      onClick={run}
      disabled={state === 'checking'}
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: accent,
        background: '#ffffff',
        border: `1px solid ${rim}`,
        borderRadius: 6,
        padding: '13px 12px',
        cursor: state === 'checking' ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {state === 'checking' ? 'Verifying…' : state === 'error' ? 'Retry verify' : 'Verify'}
    </button>
  );
}

// ── Summary card ────────────────────────────────────────────────────────────

function SummaryCard({
  count,
  label,
  fg,
  bg,
  border,
}: {
  count: number;
  label: string;
  fg: string;
  bg: string;
  border: string;
}) {
  return (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 140,
        border: `1px solid ${border}`,
        background: bg,
        borderRadius: 12,
        padding: '20px 22px',
      }}
    >
      <div style={{ fontSize: 38, fontWeight: 800, color: fg, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: inkMuted, marginTop: 8 }}>{label}</div>
    </div>
  );
}

// ── Insight row ─────────────────────────────────────────────────────────────

function InsightRow({ insight, first }: { insight: MonitorInsight; first: boolean }) {
  const sev = severityStyle(insight.severity);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0,1fr) auto',
        gap: 14,
        alignItems: 'start',
        padding: '16px 18px',
        borderTop: first ? 'none' : `1px solid ${rim}`,
      }}
    >
      <span
        style={{
          marginTop: 2,
          fontSize: 11,
          fontWeight: 700,
          color: sev.fg,
          background: sev.bg,
          border: `1px solid ${sev.border}`,
          borderRadius: 999,
          padding: '3px 10px',
          whiteSpace: 'nowrap',
        }}
      >
        {sev.label}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: ink }}>{insight.title}</div>
        <p style={{ fontSize: 13, color: inkMuted, margin: '4px 0 0', lineHeight: 1.5 }}>
          {insight.message}
        </p>
        <div style={{ fontSize: 11, color: inkSubtle, marginTop: 6 }}>
          {fmtTime(insight.created)}
        </div>
      </div>
      <div style={{ justifySelf: 'end', paddingTop: 2 }}>
        <VerifyButton verifyUrl={insight.verify_url} />
      </div>
    </div>
  );
}

// ── Alerts setup ────────────────────────────────────────────────────────────

const CHANNEL_HINT: Record<string, string> = {
  telegram: 'Your Telegram chat id (message @userinfobot to get it)',
  email: 'you@business.com',
  whatsapp: 'whatsapp:+15551234567',
};

function AlertsCard({ token }: { token: string }) {
  const [channels, setChannels] = useState<AlertChannels | null>(null);
  const [channel, setChannel] = useState('telegram');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    getAlertChannels(token)
      .then(setChannels)
      .catch(() => {});
  }, [token]);

  async function save() {
    setBusy(true);
    setErr('');
    setSaved(false);
    setTestMsg('');
    try {
      const c = await setAlertChannel(token, channel, address.trim(), true);
      setChannels(c);
      setSaved(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save your alert channel.');
    } finally {
      setBusy(false);
    }
  }

  async function fireTest() {
    setBusy(true);
    setErr('');
    setTestMsg('');
    try {
      const r = await testAlert(token);
      const sent = r.deliveries.filter((d) => d.status === 'sent').length;
      setTestMsg(
        sent > 0
          ? `Sent to ${sent} channel(s) — check your ${channel}.`
          : (r.deliveries[0]?.detail ?? r.message),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send a test alert.');
    } finally {
      setBusy(false);
    }
  }

  const configured = channels?.available.find((a) => a.kind === channel)?.configured;

  return (
    <section
      style={{ border: `1px solid ${rim}`, borderRadius: 12, padding: '20px 22px', marginTop: 28 }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 6px' }}>Get alerted</h2>
      <p style={{ fontSize: 13, color: inkMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
        Be notified the moment Monitor finds something critical — on Telegram, email, or WhatsApp.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          style={{ padding: '9px 10px', border: `1px solid ${rim}`, borderRadius: 8, fontSize: 14 }}
        >
          <option value="telegram">Telegram</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={CHANNEL_HINT[channel]}
          style={{
            flex: '1 1 220px',
            padding: '9px 12px',
            border: `1px solid ${rim}`,
            borderRadius: 8,
            fontSize: 14,
          }}
        />
        <button
          type="button"
          onClick={save}
          disabled={busy || !address.trim()}
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            background: !address.trim() ? '#9ca3af' : accent,
            border: 'none',
            borderRadius: 8,
            padding: '9px 16px',
            cursor: busy || !address.trim() ? 'default' : 'pointer',
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={fireTest}
          disabled={busy}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: accent,
            background: '#fff',
            border: `1px solid ${rim}`,
            borderRadius: 8,
            padding: '9px 16px',
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          Send test
        </button>
      </div>
      {configured === false && (
        <p style={{ fontSize: 12, color: '#b45309', margin: '10px 0 0' }}>
          This channel isn't switched on for your account yet — save it and we'll enable delivery.
        </p>
      )}
      {saved && <p style={{ fontSize: 13, color: '#15803d', margin: '10px 0 0' }}>Saved ✓</p>}
      {testMsg && <p style={{ fontSize: 13, color: inkMuted, margin: '10px 0 0' }}>{testMsg}</p>}
      {err && <p style={{ fontSize: 13, color: '#b91c1c', margin: '10px 0 0' }}>{err}</p>}
      {channels?.prefs && channels.prefs.length > 0 && (
        <p style={{ fontSize: 12, color: inkSubtle, margin: '10px 0 0' }}>
          Active: {channels.prefs.filter((p) => p.enabled).map((p) => p.kind).join(', ') || 'none'}
        </p>
      )}
    </section>
  );
}

// ── Plan / upgrade ──────────────────────────────────────────────────────────

function PlanCard() {
  return (
    <section
      style={{
        border: `1px solid ${rim}`,
        borderRadius: 12,
        padding: '20px 22px',
        marginTop: 20,
        background: '#f8fafc',
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 6px' }}>Your plan</h2>
      <p style={{ fontSize: 13, color: inkMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
        You're on <strong>Free</strong> — unlimited findings + signed proof. <strong>Business</strong>{' '}
        ($299/mo) adds automations, scheduled reports, and priority alerts.
      </p>
      <a
        href="mailto:hello@growing-intelligence.com?subject=Upgrade%20to%20Monitor%20Business"
        style={{
          display: 'inline-block',
          fontSize: 14,
          fontWeight: 700,
          color: '#fff',
          background: accent,
          borderRadius: 8,
          padding: '10px 18px',
          textDecoration: 'none',
        }}
      >
        Upgrade to Business →
      </a>
      <p style={{ fontSize: 12, color: inkSubtle, margin: '12px 0 0' }}>
        Card billing activates shortly — we'll set you up the moment it's live.
      </p>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function MonitorDashboardPage() {
  const navigate = useNavigate();
  const token = getMonitorToken();
  if (!token) return <Navigate to="/monitor/start" replace />;

  const workspace = getWorkspaceName();

  const q = useQuery({
    queryKey: ['monitor', 'client', 'insights', token],
    queryFn: () => fetchInsights(token!),
    refetchInterval: 30_000,
  });

  const summary = q.data?.summary ?? { critical: 0, warning: 0, ok: 0 };
  const insights = q.data?.insights ?? [];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#ffffff',
        color: ink,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '18px 24px',
          borderBottom: `1px solid ${rim}`,
        }}
      >
        <GiOrbitLogo size="sm" />
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Growing Intelligence</span>
        <button
          type="button"
          onClick={() => navigate('/monitor/connect')}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: accent,
            background: '#ffffff',
            border: `1px solid ${rim}`,
            borderRadius: 6,
            padding: '7px 14px',
            cursor: 'pointer',
          }}
        >
          + Send more data
        </button>
      </header>

      <main style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px 56px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px', lineHeight: 1.15 }}>
          {workspace ? `${workspace} — your findings` : 'Your findings'}
        </h1>
        <p style={{ fontSize: 15, color: inkMuted, margin: '0 0 26px', lineHeight: 1.5 }}>
          What Monitor found in your business, in plain language. Each finding is signed, so you can
          prove it.
        </p>

        {q.isPending && (
          <p style={{ fontSize: 14, color: inkSubtle }}>Loading your findings…</p>
        )}

        {q.isError && (
          <div
            style={{
              border: '1px solid #fecaca',
              background: '#fef2f2',
              borderRadius: 10,
              padding: '16px 18px',
              fontSize: 13,
              color: '#b91c1c',
            }}
          >
            We couldn't load your findings just now. We'll keep trying automatically — or{' '}
            <button
              type="button"
              onClick={() => q.refetch()}
              style={{
                fontWeight: 700,
                color: '#b91c1c',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              try again now
            </button>
            .
          </div>
        )}

        {!q.isPending && !q.isError && (
          <>
            {/* Summary cards */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
              <SummaryCard
                count={summary.critical}
                label="Need attention"
                fg="#b91c1c"
                bg="#fef2f2"
                border="#fecaca"
              />
              <SummaryCard
                count={summary.warning}
                label="Worth a look"
                fg="#b45309"
                bg="#fffbeb"
                border="#fde68a"
              />
              <SummaryCard
                count={summary.ok}
                label="Looking good"
                fg="#15803d"
                bg="#f0fdf4"
                border="#bbf7d0"
              />
            </div>

            {/* Insight list / empty state */}
            {insights.length === 0 ? (
              <div
                style={{
                  border: `1px dashed ${rim}`,
                  borderRadius: 12,
                  padding: '40px 24px',
                  textAlign: 'center',
                }}
              >
                <p style={{ fontSize: 15, color: inkMuted, margin: '0 0 16px' }}>
                  No data yet — connect a source to see your first signed insight.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/monitor/connect')}
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#ffffff',
                    background: accent,
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 18px',
                    cursor: 'pointer',
                  }}
                >
                  Connect your business →
                </button>
              </div>
            ) : (
              <div
                style={{
                  border: `1px solid ${rim}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: '#ffffff',
                }}
              >
                {insights.map((ins, i) => (
                  <InsightRow key={ins.verdict_id} insight={ins} first={i === 0} />
                ))}
              </div>
            )}
          </>
        )}

        <AlertsCard token={token!} />
        <PlanCard />

        <p style={{ fontSize: 12, color: inkSubtle, margin: '36px 0 0', lineHeight: 1.5 }}>
          Your data stays yours. Every result is signed — verify it yourself, anytime.
        </p>
      </main>
    </div>
  );
}

export default MonitorDashboardPage;
